import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ConfirmationModal,
  ServiceCartSelect,
  StyledColumn,
  StyledContent,
  StyledPageWrapper,
} from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { getTerminalError } from "@monorepo/helpers";
import { useAddServiceToCartMutation } from "@monorepo/services/api/carts";
import { setService } from "@monorepo/slices/service";
import theme from "@monorepo/styles/theme";
import { IActiveCartSliceState } from "@monorepo/types";
import { Box, Button, Typography } from "@mui/material";
import { reverse } from "named-urls";

import { useAppDispatch, useAppSelector } from "hooks";

interface IFormData {
  cart_group_id: string;
}

const ServicePrepaidForm = () => {
  const { t } = useTranslation();
  const serviceSlice = useAppSelector((state) => state.serviceSlice);
  const dispatch = useAppDispatch();
  const [addServiceToCart, addResponse] = useAddServiceToCartMutation();
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const activeCartId = useSelector(({ activeCartSlice }: IActiveCartSliceState) => activeCartSlice.cartId);
  const history = useHistory();

  useEffect(() => {
    if (!addResponse.isUninitialized) {
      if (addResponse.isError && Array.isArray(addResponse.error)) {
        setShowErrorModal(true);
      } else if (addResponse.isSuccess) {
        history.push(
          reverse(routeConfig.dashboard.payments.cartGroup.self, {
            cartGroupId: addResponse.data.data.attributes.cart_group_id,
          })
        );
      }
    }
  }, [
    addResponse.data,
    addResponse.error,
    addResponse.isError,
    addResponse.isSuccess,
    addResponse.isUninitialized,
    history,
  ]);

  const methods = useForm<IFormData>();
  const onSubmit = (data: IFormData) => {
    if (serviceSlice && serviceSlice.parameters) {
      let params: { [index: string]: string } = {};
      Object.keys(serviceSlice.parameters).forEach((x) => {
        if (serviceSlice?.parameters?.length && serviceSlice?.parameters[Number(x)]) {
          const serviceParameter = serviceSlice.parameters[Number(x)];
          params[serviceParameter.parameter_name] = serviceParameter.value;
        }
      });
      addServiceToCart({
        paid: true,
        pay_now: false,
        service_id: serviceSlice.id,
        service_name: serviceSlice.api_service_name,
        cart_group_id: data.cart_group_id,
        params: params!,
      })
        .unwrap()
        .catch((err) => {
          const terminalError = getTerminalError(err);
          if (!!terminalError) {
            toast.error(terminalError);
          }
        });
    }
  };

  const handleContinue = () => {
    dispatch(setService({ ...serviceSlice, import_date: undefined }));
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <StyledPageWrapper>
            <StyledContent>
              <StyledColumn>
                <Typography textAlign="center">{t("payments.import_date_found")}</Typography>
                <Typography textAlign="center" fontWeight="bold">
                  {serviceSlice && serviceSlice.import_date}
                </Typography>
                <Typography textAlign="center">{t("payments.prepaid_info")}</Typography>
                <ServiceCartSelect fieldName={"cart_group_id"} defaultValue={activeCartId} />
              </StyledColumn>
            </StyledContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                rowGap: { xs: theme.spacing(1) },
                columnGap: { sm: theme.spacing(1) },
                justifyContent: "center",
              }}
            >
              <Button
                disabled={addResponse.isLoading || methods.formState.isSubmitting}
                type="submit"
                variant="contained"
                color="secondary"
                disableElevation
              >
                {t("cart.save")}
              </Button>
              <Button onClick={handleContinue} variant="contained" disableElevation>
                {t("common.continue")}
              </Button>
            </Box>
          </StyledPageWrapper>
        </form>
      </FormProvider>
      {showErrorModal && serviceSlice && Array.isArray(addResponse.error) && (
        <ConfirmationModal
          open={showErrorModal}
          confirmButtonText={t("common.override")}
          declineButtonText={t("common.edit")}
          onClose={() => setShowErrorModal(false)}
        >
          {addResponse.error[0].title}
        </ConfirmationModal>
      )}
    </>
  );
};

export default ServicePrepaidForm;
